(function () {
  const VARIANT_GID_PREFIX = 'gid://shopify/ProductVariant/';
  const REQUEST_TIMEOUT = 10000;

  const cache = new Map();
  const inflight = new Map();

  const DEBUG = false;

  function log(...args) {
    if (DEBUG) {
      console.log('[CraftCellars Storefront API]', ...args);
    }
  }

  function normalizeLocationId(id) {
    if (id == null || id === '') return '';

    const str = String(id);

    const gidMatch = str.match(/Location\/(\d+)/i);
    if (gidMatch) return gidMatch[1];

    const digits = str.replace(/\D/g, '');

    return digits || str;
  }

  function getClient() {
    const config = window.craftCellarsLocationConfig?.storefront;

    if (!config?.accessToken) {
      log('No Storefront API access token found');
      return null;
    }

    const shopUrl = (window.shopUrl || '').replace(/\/$/, '');

    if (!shopUrl) {
      log('window.shopUrl missing');
      return null;
    }

    return {
      endpoint: `${shopUrl}/api/${config.apiVersion}/graphql.json`,
      accessToken: config.accessToken,
    };
  }

  function toVariantGid(variantId) {
    const id = String(variantId);

    return id.startsWith('gid://') ? id : `${VARIANT_GID_PREFIX}${id}`;
  }

  function normalizeAvailabilityNodes(nodes = []) {
    return nodes.map(node => ({
      location_id: normalizeLocationId(node?.location?.id),
      location_name: node?.location?.name || '',
      available: Boolean(node?.available),

      // quantityAvailable may not exist in some Storefront APIs
      quantity_available:
        typeof node?.quantityAvailable === 'number' ? node.quantityAvailable : null,
    }));
  }

  function isInStock(availability) {
    if (!availability) return false;

    if (typeof availability.quantity_available === 'number') {
      return availability.quantity_available > 0;
    }

    return Boolean(availability.available);
  }

  function normalizeName(name) {
    return String(name || '')
      .toLowerCase()
      .trim();
  }

  function findAvailability(availabilities, locationConfig, shopLocations) {
    if (!availabilities?.length || !locationConfig) return null;

    const targetId = normalizeLocationId(locationConfig.id);
    if (targetId) {
      const byId = availabilities.find(
        entry => normalizeLocationId(entry.location_id) === targetId
      );
      if (byId) return byId;
    }

    const namesToMatch = [
      locationConfig.name,
      locationConfig.shopifyName,
      locationConfig.label,
      locationConfig.address,
    ]
      .filter(Boolean)
      .map(normalizeName);

    const matchedAvailability = availabilities.find(entry =>
      namesToMatch.some(
        name =>
          normalizeName(entry.location_name) === name ||
          normalizeName(entry.location_name).includes(name) ||
          name.includes(normalizeName(entry.location_name))
      )
    );

    if (matchedAvailability) return matchedAvailability;

    if (shopLocations?.length) {
      const matchedShop = shopLocations.find(sl => namesToMatch.includes(normalizeName(sl.name)));
      if (matchedShop) {
        const byShopId = availabilities.find(
          entry => normalizeLocationId(entry.location_id) === normalizeLocationId(matchedShop.id)
        );
        if (byShopId) return byShopId;
      }
    }

    return null;
  }

  function quantityFromAvailability(entry) {
    if (!entry) return null;
    if (typeof entry.quantity_available === 'number') {
      return entry.quantity_available;
    }
    if (entry.available === true) return 1;
    if (entry.available === false) return 0;
    return null;
  }

  /**
   * Pickup (location_2): stock from the Shopify location entry only.
   */
  function resolveConfiguredLocationAvailability(
    availabilities,
    locationConfig,
    shopLocations,
    locationKey,
    fallbackName
  ) {
    const entry = locationConfig
      ? findAvailability(availabilities, locationConfig, shopLocations)
      : null;

    const rawQty = quantityFromAvailability(entry);
    const quantity = typeof rawQty === 'number' ? Math.max(0, rawQty) : rawQty === null ? null : 0;

    return {
      locationKey,
      location_id: normalizeLocationId(locationConfig?.id || entry?.location_id),
      location_name:
        locationConfig?.name || locationConfig?.shopifyName || entry?.location_name || fallbackName,
      available: typeof quantity === 'number' ? quantity > 0 : false,
      quantity_available: quantity,
      source: 'location_entry',
    };
  }

  /**
   * Pickup (location_2): stock from the matching Shopify location entry.
   */
  function resolvePickupAvailability(availabilities, config, shopLocations) {
    return resolveConfiguredLocationAvailability(
      availabilities,
      config?.locations?.location_2,
      shopLocations,
      'location_2',
      'Pickup'
    );
  }

  /**
   * Shipping (location_1): stock from the matching Shopify location entry.
   */
  function resolveShippingAvailability(availabilities, config, shopLocations) {
    return resolveConfiguredLocationAvailability(
      availabilities,
      config?.locations?.location_1,
      shopLocations,
      'location_1',
      'Shipping'
    );
  }

  /**
   * Resolves shipping and pickup independently; one being out does not change the other.
   */
  function resolveBothLocationsInventory(availabilities, config, shopLocations, totalQuantity) {
    const pickup = resolvePickupAvailability(availabilities, config, shopLocations);
    const shipping = resolveShippingAvailability(availabilities, config, shopLocations);

    return {
      location_1: shipping,
      location_2: pickup,
      byKey: {
        location_1: shipping,
        location_2: pickup,
      },
    };
  }

  function getAvailabilityForSelection(resolved, selectionKey) {
    if (!resolved?.byKey || !selectionKey) return null;
    return resolved.byKey[selectionKey] ?? null;
  }

  async function fetchVariantAvailabilities(variantId) {
    const client = getClient();

    if (!client) return null;

    const cacheKey = String(variantId);

    function normalizeCachedPayload(cached) {
      if (Array.isArray(cached)) {
        return { availabilities: cached, variantQuantityAvailable: null };
      }
      return cached;
    }

    // Cache hit
    if (cache.has(cacheKey)) {
      log('Cache hit', cacheKey);

      return normalizeCachedPayload(cache.get(cacheKey));
    }

    // Reuse inflight request
    if (inflight.has(cacheKey)) {
      log('Using inflight request', cacheKey);

      return inflight.get(cacheKey).then(normalizeCachedPayload);
    }

    const query = `
      query VariantStoreAvailability($id: ID!) {
        node(id: $id) {
          ... on ProductVariant {
            quantityAvailable
            storeAvailability(first: 20) {
              nodes {
                available
                quantityAvailable
                location {
                  id
                  name
                }
              }
            }
          }
        }
      }
    `;

    const variables = {
      id: toVariantGid(variantId),
    };

    log('Request started', {
      endpoint: client.endpoint,
      variables,
    });

    const controller = new AbortController();

    const timeoutId = setTimeout(() => {
      controller.abort();
    }, REQUEST_TIMEOUT);

    const request = fetch(client.endpoint, {
      method: 'POST',

      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': client.accessToken,
      },

      body: JSON.stringify({
        query,
        variables,
      }),

      signal: controller.signal,
    })
      .then(async response => {
        clearTimeout(timeoutId);

        log('HTTP Status', response.status);

        if (!response.ok) {
          throw new Error(`Storefront API HTTP ${response.status}`);
        }

        return response.json();
      })
      .then(payload => {
        log('Full response', payload);

        if (payload?.errors?.length) {
          console.error('Storefront GraphQL errors', payload.errors);

          throw new Error(payload.errors.map(e => e.message).join('; '));
        }

        const variantNode = payload?.data?.node;
        const nodes = variantNode?.storeAvailability?.nodes || [];

        const result = {
          availabilities: normalizeAvailabilityNodes(nodes),
          variantQuantityAvailable:
            typeof variantNode?.quantityAvailable === 'number'
              ? variantNode.quantityAvailable
              : null,
        };

        log('Normalized inventory payload', result);

        cache.set(cacheKey, result);

        return result;
      })
      .catch(error => {
        if (error.name === 'AbortError') {
          console.error('Storefront API request timeout');
        } else {
          console.error('Storefront API fetch failed', error);
        }

        throw error;
      })
      .finally(() => {
        clearTimeout(timeoutId);

        inflight.delete(cacheKey);
      });

    inflight.set(cacheKey, request);

    return request;
  }

  window.CraftCellarsStorefrontApi = {
    isEnabled() {
      return Boolean(getClient());
    },

    normalizeLocationId,

    isInStock,

    findAvailability,

    resolvePickupAvailability,

    resolveShippingAvailability,

    resolveBothLocationsInventory,

    getAvailabilityForSelection,

    fetchVariantAvailabilities,

    clearCache() {
      cache.clear();
      inflight.clear();

      log('Cache cleared');
    },
  };

  log('location-storefront-api.js loaded');
})();
